/**
 * CrystalSpineVisualEngine — Cinematic GEMSTONE spine for the
 * Spinal Healing & Recovery immersion.
 *
 * Each vertebra is a faceted diamond crystal suspended in deep space,
 * lit from within by a golden light that pours from a divine starburst
 * source above the crown. Lens-flare rays fan out from every gem,
 * Saturn halos gently orbit them, prismatic rainbow highlights catch
 * the crystal edges, and tiny particles orbit each vertebra like moons.
 *
 * Six-phase timeline (unchanged):
 *   arrival · bath · decompress · site heal · nerve relief · integration
 */

import { useMemo, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const VERTEBRAE = [
  { id: 'L1', y: 0.06, size: 0.90 },
  { id: 'L2', y: 0.21, size: 0.98 },
  { id: 'L3', y: 0.36, size: 1.06 },
  { id: 'L4', y: 0.51, size: 1.10 },
  { id: 'L5', y: 0.66, size: 1.06 },
];
const SACRUM_Y = 0.82; // Bigger diamond at the base — the sacrum.
const SURGERY_INDEX = 3; // L4

const T_ARRIVAL_END   = 45 / 420;
const T_BATH_END      = 120 / 420;
const T_DECOMP_END    = 210 / 420;
const T_SITE_HEAL_END = 300 / 420;
const T_NERVE_END     = 360 / 420;

// Reusable — draws a faceted diamond gem shape (side view). Points from
// the top vertex, out to the shoulders (widest point ~35% down), then
// converging back to a bottom vertex.
const gemPath = (cx, cy, w, h) => {
  const shoulderY = cy - h * 0.15;
  const bottomY   = cy + h * 0.55;
  const topY      = cy - h * 0.55;
  const halfW     = w / 2;
  const innerW    = w * 0.28;
  return `
    M ${cx} ${topY}
    L ${cx - halfW} ${shoulderY}
    L ${cx - innerW} ${cy + h * 0.05}
    L ${cx} ${bottomY}
    L ${cx + innerW} ${cy + h * 0.05}
    L ${cx + halfW} ${shoulderY}
    Z
  `;
};

const CrystalSpineVisualEngine = ({ elapsed = 0, totalDuration = 420, isActive = false, narrationTarget = null }) => {
  const [breath, setBreath] = useState(0.5);
  const [waveY, setWaveY] = useState(60);
  const [crownRelease, setCrownRelease] = useState(0);
  const [sacralBloom, setSacralBloom] = useState(0);
  const [cycles, setCycles] = useState(0);
  // Which vertebra index (0..5) is currently being transformed. -1 = travelling
  // between stations, or before crown opens, or after the sequence completes.
  const [dwellIdx, setDwellIdx] = useState(-1);
  // Growing set of vertebrae the light has already fully awakened.
  // Once all 6 are here, we enter the cathedral finale.
  const [transformed, setTransformed] = useState(() => new Set());
  // Cathedral 0..1 — final unified column glow that appears once every
  // vertebra has transformed. Blooms slowly (~5s) and never leaves.
  const [cathedral, setCathedral] = useState(0);
  // Continuous channel-fill (0..1) — the "second part" of the energy
  // flow. Rises SMOOTHLY over the whole descent instead of popping in
  // discrete jumps as each vertebra transforms, so the channel of
  // light appears to fill gradually and magically.
  const [channelBoost, setChannelBoost] = useState(0);
  const channelBoostRef = useRef(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const prevWtRef = useRef(0);
  // ── Crystal chime · Web Audio synthesis ────────────────────────
  // A soft ascending pentatonic bell rings the instant each vertebra
  // transforms — locking the golden bloom visual to a tactile audio
  // cue. Synthesised in-browser (3 sine partials + exp decay) so we
  // don't ship any audio files or hit object storage.
  const audioCtxRef = useRef(null);
  const lastChimedSizeRef = useRef(0);
  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      audioCtxRef.current = new AC();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_) { /* noop */ } }
    return ctx;
  };
  const playCrystalChime = (index) => {
    try {
      const ctx = getAudioCtx();
      if (!ctx) return;
      const now = ctx.currentTime;
      // Ascending pentatonic (C5, D5, E5, G5, A5, C6) — feels like
      // consciousness rising as each vertebra awakens.
      const scale = [523.25, 587.33, 659.25, 783.99, 880.00, 1046.50];
      const freq = scale[Math.min(Math.max(index, 0), scale.length - 1)];
      // Master gain (kept low — sits gently under Michael's affirmations)
      const master = ctx.createGain();
      master.gain.value = 0.35;
      master.connect(ctx.destination);
      // 3 sine partials for crystalline shimmer + envelope
      const partials = [
        { mult: 1.0,  peak: 0.20, decay: 3.2 },
        { mult: 2.01, peak: 0.09, decay: 2.4 },
        { mult: 3.02, peak: 0.04, decay: 1.7 },
      ];
      partials.forEach(({ mult, peak, decay }) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq * mult;
        g.gain.setValueAtTime(0.0001, now);
        g.gain.linearRampToValueAtTime(peak, now + 0.010);          // fast attack
        g.gain.exponentialRampToValueAtTime(0.0001, now + decay);   // long exp decay
        osc.connect(g).connect(master);
        osc.start(now);
        osc.stop(now + decay + 0.05);
      });
    } catch (_) { /* audio blocked — silent fail */ }
  };
  // Chime whenever a NEW vertebra joins the transformed set.
  useEffect(() => {
    const size = transformed.size;
    if (size > lastChimedSizeRef.current) {
      // Newest index = highest number in the set (added in ascending order).
      let newest = -1;
      transformed.forEach((v) => { if (v > newest) newest = v; });
      if (newest >= 0) playCrystalChime(newest);
    }
    lastChimedSizeRef.current = size;
  }, [transformed]);
  // Reset chime tracking when the journey restarts (transformed cleared).
  useEffect(() => {
    if (!isActive) { lastChimedSizeRef.current = 0; }
  }, [isActive]);
  // Close the AudioContext when the component unmounts.
  useEffect(() => () => {
    try { audioCtxRef.current && audioCtxRef.current.close(); } catch (_) { /* noop */ }
    audioCtxRef.current = null;
  }, []);
  const progress = Math.min(1, elapsed / totalDuration);

  useEffect(() => {
    if (!isActive) return;
    startRef.current = Date.now();

    // ── Cathedral of Light schedule ────────────────────────────────
    // A slow, deliberate, one-shot descent. The pulse pauses at every
    // vertebra long enough for its unique transformation to complete
    // — L1 blooms, L2 spirals, L3 radiates, L4 expands, L5 gently
    // explodes, S1 grounds. When all six have awakened, the whole
    // column enters the cathedral state and remains there.
    const CROWN_OPEN_MS  = 8000;   // slow blossom of the crown
    const TRAVEL_MS      = 4000;   // ribbon between stations
    const DWELL_MS       = 6500;   // full transformation at each vertebra
    const FINAL_HOLD_MS  = 4000;   // pause at sacrum before cathedral

    // Y coords at scale-560 basis (see per-vertebra render), computed
    // once so the timing lines up precisely with the positioned gems.
    const stationY = (i) => 100 + [0.05, 0.20, 0.36, 0.53, 0.70, 0.86][i] * 560;

    const tick = () => {
      const now = Date.now();
      const el = now - startRef.current;

      // Deep breath cycle stays continuous throughout.
      setBreath(0.5 + 0.5 * Math.sin(((el % 14000) / 14000) * Math.PI * 2 - Math.PI / 2));

      // ── SMOOTH CHANNEL FILL — continuous 0..1 that rises steadily
      // throughout Phase B (never jumps in discrete steps).
      // Travel adds a small ramp; each dwell adds the bulk of that
      // station's contribution with a smoothstep curve. Result: the
      // channel of light appears to WIDEN GRADUALLY as energy flows.
      let targetFill = 0;
      if (el >= CROWN_OPEN_MS) {
        let cursor2 = CROWN_OPEN_MS;
        let stationsDone = 6; // default = past all stations
        let stationInProgress = 0;
        for (let i = 0; i < 6; i++) {
          const travelStart = cursor2;
          const dwellStart  = travelStart + TRAVEL_MS;
          const dwellEnd    = dwellStart  + DWELL_MS;
          if (el >= travelStart && el < dwellStart) {
            stationsDone = i;
            const p = (el - travelStart) / TRAVEL_MS;
            const eased = p * p * (3 - 2 * p);
            stationInProgress = eased * 0.25;   // gentle ramp on approach
            break;
          }
          if (el >= dwellStart && el < dwellEnd) {
            stationsDone = i;
            const dp = (el - dwellStart) / DWELL_MS;
            const eased = dp * dp * (3 - 2 * dp);
            stationInProgress = 0.25 + eased * 0.75; // bulk during dwell
            break;
          }
          cursor2 = dwellEnd;
        }
        targetFill = Math.min(1, (stationsDone + stationInProgress) / 6);
      }
      // Frame-rate-friendly lerp toward target for extra softness.
      const prev = channelBoostRef.current;
      const smoothed = prev + (targetFill - prev) * 0.05;
      channelBoostRef.current = smoothed;
      setChannelBoost(smoothed);

      // ── Phase A · crown opens ───────────────────────────────
      if (el < CROWN_OPEN_MS) {
        const p = el / CROWN_OPEN_MS;
        // Sun blossom: rises smoothly, holds near the top, decays slowly.
        setCrownRelease(p < 0.6 ? p / 0.6 : Math.max(0.35, 1 - (p - 0.6) / 1.2));
        setWaveY(60);
        setDwellIdx(-1);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // ── Phase B · travel-dwell alternating through 6 stations ─
      let cursor = CROWN_OPEN_MS;
      for (let i = 0; i < 6; i++) {
        const travelStart = cursor;
        const dwellStart  = travelStart + TRAVEL_MS;
        const dwellEnd    = dwellStart  + DWELL_MS;
        if (el >= travelStart && el < dwellStart) {
          // Ribbon descending toward station i. Smoothstep.
          const yFrom = i === 0 ? 60 : stationY(i - 1);
          const yTo   = stationY(i);
          const p = (el - travelStart) / TRAVEL_MS;
          const eased = p * p * (3 - 2 * p);
          setWaveY(yFrom + eased * (yTo - yFrom));
          setDwellIdx(-1);
          setCrownRelease(Math.max(0, 0.35 - (el - CROWN_OPEN_MS) / 30000));
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        if (el >= dwellStart && el < dwellEnd) {
          // Dwell at station i — light is absorbing, transforming.
          setWaveY(stationY(i));
          setDwellIdx(i);
          setCrownRelease(Math.max(0, 0.3 - (el - CROWN_OPEN_MS) / 30000));
          // Mark this vertebra as transformed at 75% through the dwell.
          const dwellP = (el - dwellStart) / DWELL_MS;
          if (dwellP > 0.75) {
            setTransformed((prev) => {
              if (prev.has(i)) return prev;
              const next = new Set(prev);
              next.add(i);
              // Channel width tracks transformed count.
              setCycles(next.size);
              return next;
            });
          }
          // Sacral bloom activates during the final station's dwell.
          if (i === 5) {
            setSacralBloom(dwellP < 0.4 ? dwellP / 0.4 : 1);
          }
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        cursor = dwellEnd;
      }

      // ── Phase C · cathedral ─────────────────────────────────
      const cathedralStart = cursor + FINAL_HOLD_MS;
      if (el < cathedralStart) {
        setWaveY(stationY(5));
        setDwellIdx(-1);
        setSacralBloom(1);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const cathT = el - cathedralStart;
      setCathedral(Math.min(1, cathT / 5000));
      setWaveY(stationY(5));
      setDwellIdx(-1);
      setSacralBloom(1);
      setCrownRelease(0.15 + 0.1 * Math.sin(cathT / 1200));

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive]);

  const phase = useMemo(() => {
    if (progress < T_ARRIVAL_END)   return 'arrival';
    if (progress < T_BATH_END)      return 'bath';
    if (progress < T_DECOMP_END)    return 'decompress';
    if (progress < T_SITE_HEAL_END) return 'site_heal';
    if (progress < T_NERVE_END)     return 'nerve_relief';
    return 'integration';
  }, [progress]);

  const arrivalGlow = phase === 'arrival' ? progress / T_ARRIVAL_END : 1;
  const decompressAmount = useMemo(() => {
    if (phase === 'arrival' || phase === 'bath') return 0;
    if (phase === 'decompress') return Math.min(1, (progress - T_BATH_END) / (T_DECOMP_END - T_BATH_END));
    return 1;
  }, [phase, progress]);
  const siteHealAmount = useMemo(() => {
    if (['arrival', 'bath', 'decompress'].includes(phase)) return 0;
    if (phase === 'site_heal') return Math.min(1, (progress - T_DECOMP_END) / (T_SITE_HEAL_END - T_DECOMP_END));
    return 1;
  }, [phase, progress]);
  const nerveCalmAmount = useMemo(() => {
    if (['arrival', 'bath', 'decompress', 'site_heal'].includes(phase)) return 0;
    if (phase === 'nerve_relief') return Math.min(1, (progress - T_SITE_HEAL_END) / (T_NERVE_END - T_SITE_HEAL_END));
    return 1;
  }, [phase, progress]);

  const vertebraOffset = (i) => {
    if (decompressAmount === 0) return 0;
    const be = 0.5 + breath * 0.5;
    const distFromSite = i - (SURGERY_INDEX);
    const dir = Math.sign(distFromSite || 1);
    return dir * Math.min(2.5, Math.abs(distFromSite) * 0.85) * decompressAmount * be * 3.2;
  };

  // Cumulative "gold saturation" grows with each vertebra transformed.
  // Now scales 0..1 across 6 transformations (was 5 cycles).
  const goldSaturation = Math.min(1, cycles / 6);
  // NOTE: `channelBoost` is now a smooth state value (updated every
  // frame in the RAF tick). Do NOT redefine it as a discrete const
  // here — the channel of light must widen GRADUALLY.

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="crystal-spine-engine">
      {/* Pure void background */}
      <div className="absolute inset-0" style={{ background: '#020104' }} />
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 70% 55% at 50% 42%, rgba(60,40,10,0.35) 0%, rgba(20,15,10,0.15) 40%, transparent 75%)',
      }} />

      {/* Distant starfield */}
      {[...Array(45)].map((_, i) => {
        const seed = i * 137.508;
        const x = ((seed) % 100);
        const y = ((seed * 1.3) % 100);
        const size = 0.6 + (i % 4) * 0.4;
        return (
          <motion.div
            key={`s-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: size, height: size,
              left: `${x}%`, top: `${y}%`,
              background: 'rgba(255,245,220,0.9)',
              boxShadow: '0 0 3px rgba(255,235,180,0.5)',
            }}
            animate={{ opacity: [0.15, 0.9, 0.15] }}
            transition={{ duration: 4 + (i % 5), repeat: Infinity, delay: (i % 7) * 0.9, ease: 'easeInOut' }}
          />
        );
      })}

      {/* ═════════ DIVINE LIGHT SOURCE — starburst above the crown ═════════ */}
      <div
        className="absolute pointer-events-none"
        style={{ left: '50%', top: '4%', transform: 'translateX(-50%)' }}
      >
        {/* Crown bloom — the moment the pulse reaches the sun, this ring
            expands outward in silence, scattering soft radiance. */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 260 + crownRelease * 240,
            height: 260 + crownRelease * 240,
            left: -130 - crownRelease * 120,
            top: -130 - crownRelease * 120,
            background: 'radial-gradient(circle, rgba(255,240,200,0.5) 0%, rgba(220,175,90,0.15) 50%, transparent 75%)',
            filter: `blur(${18 + crownRelease * 12}px)`,
            opacity: crownRelease * 0.9,
          }}
        />
        {/* Outer sun-flare halo */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 240 + breath * 30 + crownRelease * 60,
            height: 240 + breath * 30 + crownRelease * 60,
            left: -120 - breath * 15 - crownRelease * 30,
            top: -120 - breath * 15 - crownRelease * 30,
            background: 'radial-gradient(circle, rgba(255,235,180,0.6) 0%, rgba(220,175,90,0.25) 35%, transparent 65%)',
            filter: 'blur(18px)',
          }}
          animate={{ opacity: [0.6, 0.9, 0.6] * arrivalGlow || 0.7 }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Bright sun core — brightens dramatically on bloom */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 46 + breath * 6 + crownRelease * 20,
            height: 46 + breath * 6 + crownRelease * 20,
            left: -23 - breath * 3 - crownRelease * 10,
            top: -23 - breath * 3 - crownRelease * 10,
            background: 'radial-gradient(circle, rgba(255,250,225,1) 0%, rgba(255,220,140,0.85) 40%, rgba(200,140,60,0.3) 80%)',
            boxShadow: `0 0 ${60 + crownRelease * 80}px rgba(255,220,140,${0.85 + crownRelease * 0.15}), 0 0 ${120 + crownRelease * 120}px rgba(255,190,90,${0.5 + crownRelease * 0.4})`,
          }}
        />
        {/* 8-point starburst rays — extend during bloom */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <motion.div
            key={deg}
            className="absolute origin-center"
            style={{
              width: 260 + breath * 40 + crownRelease * 200,
              height: 2,
              left: -130 - breath * 20 - crownRelease * 100,
              top: -1,
              background: `linear-gradient(90deg, transparent 0%, rgba(255,235,180,0.65) 45%, rgba(255,220,140,${0.9 + crownRelease * 0.1}) 50%, rgba(255,235,180,0.65) 55%, transparent 100%)`,
              transform: `rotate(${deg}deg)`,
              filter: `blur(${1.5 + crownRelease * 1}px)`,
              opacity: 0.5 + crownRelease * 0.4,
            }}
            animate={{ opacity: [0.5, 0.85, 0.5] }}
            transition={{ duration: 4.5, repeat: Infinity, delay: (deg / 360) * 0.8, ease: 'easeInOut' }}
          />
        ))}
        {/* Crown release droplet — a small bright orb pours out of the
            sphere at the start of every cycle and streams downward
            toward the top of the spine (visible ~2s). */}
        {crownRelease > 0.05 && (
          <motion.div
            className="absolute rounded-full"
            style={{
              width: 12 + crownRelease * 6,
              height: 12 + crownRelease * 6,
              left: -6 - crownRelease * 3,
              top: 30 + (1 - crownRelease) * 80,
              background: 'radial-gradient(circle, rgba(255,250,225,1) 0%, rgba(255,220,140,0.85) 50%, transparent 100%)',
              boxShadow: '0 0 22px rgba(255,220,140,0.85)',
              opacity: crownRelease,
            }}
          />
        )}
      </div>

      {/* CATHEDRAL OF LIGHT — a soft golden glow along the spine that
          gradually brightens as more vertebrae transform.
          Feb 25 2026 · Michael's ask: "make the actual visual effect of
          the light pouring into the spine more impressive — more bolder,
          thicker light." The three layers below are now widened,
          brightened, and topped with an explicit "pouring column" so the
          light reads as a real cascade descending into the spine, not a
          faint aura. Base widths / opacities roughly doubled. */}
      {/* Broad soft aura — the outer halo of the glow */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: '50%', top: '4%', bottom: '10%',
          transform: 'translateX(-50%)',
          width: 320 + channelBoost * 140 + breath * 30,
          background: 'radial-gradient(ellipse at center, rgba(255,235,180,0.55) 0%, rgba(240,200,120,0.32) 35%, rgba(210,160,80,0.14) 65%, transparent 90%)',
          filter: `blur(${34 + channelBoost * 22}px)`,
          borderRadius: '50%',
          opacity: 0.3 + channelBoost * (0.5 + arrivalGlow * 0.15 + breath * 0.05),
        }}
      />
      {/* Warm inner glow — brightens with cumulative healing, still
          heavily blurred so it never reads as a solid beam. */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: '50%', top: '4%', bottom: '10%',
          transform: 'translateX(-50%)',
          width: 160 + channelBoost * 100 + breath * 18,
          background: 'linear-gradient(180deg, rgba(255,245,215,0.85) 0%, rgba(245,215,150,0.65) 30%, rgba(220,180,110,0.42) 65%, rgba(180,140,80,0.15) 90%)',
          filter: `blur(${22 + channelBoost * 16}px)`,
          borderRadius: '50%',
          opacity: 0.28 + channelBoost * (0.6 + arrivalGlow * 0.15 + breath * 0.06),
          mixBlendMode: 'screen',
        }}
      />
      {/* POURING COLUMN — the actual "light pouring in" layer.
          A vertical golden pillar with a bright core and soft edges,
          living just above the vertebrae so the viewer sees a real
          cascade descending from the crown into the spine. Feb 25 2026. */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: '50%', top: '3%', bottom: '10%',
          transform: 'translateX(-50%)',
          width: 74 + channelBoost * 56 + breath * 10,
          background:
            'linear-gradient(180deg, rgba(255,252,235,0.95) 0%, rgba(255,240,195,0.85) 22%, rgba(255,220,145,0.7) 52%, rgba(230,180,90,0.42) 82%, rgba(190,140,70,0.12) 100%)',
          filter: `blur(${9 + channelBoost * 6}px)`,
          borderRadius: '60px',
          opacity: 0.38 + channelBoost * (0.55 + arrivalGlow * 0.1 + breath * 0.04),
          mixBlendMode: 'screen',
        }}
        animate={{ opacity: [0.44, 0.72, 0.44] }}
        transition={{ duration: 5.2, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* POURING COLUMN — bright core stripe. Narrow, high-opacity
          rope of light down the centre-line so the "pouring" is
          unmistakable even before the wave arrives. */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: '50%', top: '3%', bottom: '10%',
          transform: 'translateX(-50%)',
          width: 22 + channelBoost * 18 + breath * 4,
          background:
            'linear-gradient(180deg, rgba(255,255,245,1) 0%, rgba(255,245,210,0.95) 30%, rgba(255,225,160,0.75) 60%, rgba(230,180,90,0.35) 90%, transparent 100%)',
          filter: `blur(${3 + channelBoost * 2}px)`,
          borderRadius: '40px',
          opacity: 0.55 + channelBoost * (0.4 + breath * 0.05),
          mixBlendMode: 'screen',
          boxShadow: `0 0 ${40 + channelBoost * 40}px rgba(255,225,160,${0.55 + channelBoost * 0.35}), 0 0 ${90 + channelBoost * 60}px rgba(255,200,120,${0.35 + channelBoost * 0.3})`,
        }}
        animate={{ opacity: [0.6, 0.9, 0.6] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* ═════════ THE GEMSTONE SPINE ═════════ */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg viewBox="0 0 400 800" style={{ width: '340px', height: '88vh', maxHeight: 820 }} fill="none">
          <defs>
            {/* Diamond crystal facet gradient — bright top, dark bottom */}
            <linearGradient id="diamondFace" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"  stopColor="rgba(255,250,225,0.95)" />
              <stop offset="35%" stopColor="rgba(255,225,155,0.7)" />
              <stop offset="75%" stopColor="rgba(180,140,80,0.35)" />
              <stop offset="100%" stopColor="rgba(80,55,25,0.15)" />
            </linearGradient>
            {/* Alt face — used for right side (subtle asymmetry) */}
            <linearGradient id="diamondFaceR" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"  stopColor="rgba(255,240,200,0.75)" />
              <stop offset="40%" stopColor="rgba(220,175,110,0.5)" />
              <stop offset="100%" stopColor="rgba(60,45,25,0.15)" />
            </linearGradient>
            {/* Inner golden core glow */}
            <radialGradient id="gemCore" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="rgba(255,255,235,1)" />
              <stop offset="35%" stopColor="rgba(255,220,140,0.85)" />
              <stop offset="80%" stopColor="rgba(200,140,60,0.25)" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            {/* Prismatic rainbow highlight — very subtle */}
            <linearGradient id="prism" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"  stopColor="rgba(255,180,180,0.0)" />
              <stop offset="25%" stopColor="rgba(255,180,180,0.6)" />
              <stop offset="50%" stopColor="rgba(180,220,255,0.6)" />
              <stop offset="75%" stopColor="rgba(200,255,200,0.5)" />
              <stop offset="100%" stopColor="rgba(255,220,180,0.0)" />
            </linearGradient>
            {/* Wide gem halo */}
            <radialGradient id="gemHalo" cx="50%" cy="50%" r="55%">
              <stop offset="0%"  stopColor="rgba(255,225,150,0.5)" />
              <stop offset="55%" stopColor="rgba(220,170,80,0.15)" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            {/* Saturn ring */}
            <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="rgba(255,225,155,0)" />
              <stop offset="20%"  stopColor="rgba(255,225,155,0.55)" />
              <stop offset="50%"  stopColor="rgba(255,240,190,0.85)" />
              <stop offset="80%"  stopColor="rgba(255,225,155,0.55)" />
              <stop offset="100%" stopColor="rgba(255,225,155,0)" />
            </linearGradient>
            {/* Nerve calmed */}
            <radialGradient id="nerveCalmed" cx="50%" cy="50%" r="60%">
              <stop offset="0%"  stopColor="rgba(230,245,255,0.9)" />
              <stop offset="55%" stopColor="rgba(160,210,240,0.65)" />
              <stop offset="100%" stopColor="rgba(80,120,180,0.15)" />
            </radialGradient>
            <radialGradient id="nerveInflamed" cx="50%" cy="50%" r="60%">
              <stop offset="0%"  stopColor="rgba(255,120,90,0.9)" />
              <stop offset="55%" stopColor="rgba(200,60,40,0.6)" />
              <stop offset="100%" stopColor="rgba(120,20,10,0.1)" />
            </radialGradient>
            {/* Blur filters */}
            <filter id="gemGlow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="3" />
            </filter>
            <filter id="wideGemGlow" x="-200%" y="-200%" width="500%" height="500%">
              <feGaussianBlur stdDeviation="12" />
            </filter>
          </defs>

          {/* ─── LIQUID-LIGHT COLUMN carrying the energy downward ───
              Only downward-moving currents now — this must clearly read
              as "one continuous stream descending from the crown".
              Widens as cumulative healing (`channelBoost`) grows. */}
          <line
            x1="200" y1="60" x2="200" y2="720"
            stroke={`rgba(255,235,180,${0.55 + channelBoost * 0.2})`}
            strokeWidth={0.7 + channelBoost * 1.2}
            filter="url(#gemGlow)"
          />
          {/* Primary downward current — thicker dashes.
              Starts as a delicate stream, grows richer as vertebrae light up. */}
          <motion.line
            x1="200" y1="60" x2="200" y2="720"
            stroke={`rgba(255,220,150,${0.75 + channelBoost * 0.25})`}
            strokeWidth={0.6 + channelBoost * 2.2}
            strokeDasharray="4 22"
            strokeLinecap="round"
            filter="url(#gemGlow)"
            animate={{ strokeDashoffset: [-26, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'linear' }}
          />
          {/* Secondary downward flow — finer, slower */}
          <motion.line
            x1="200" y1="60" x2="200" y2="720"
            stroke={`rgba(255,245,210,${0.6 + channelBoost * 0.25})`}
            strokeWidth={0.7 + channelBoost * 0.9}
            strokeDasharray="2 32"
            strokeLinecap="round"
            animate={{ strokeDashoffset: [-34, 0] }}
            transition={{ duration: 11, repeat: Infinity, ease: 'linear' }}
          />

          {/* ─── SPIRAL RIBBONS wrapping the spine (invisible ↔ visible) ─── */}
          {[0, 1].map((k) => {
            const amp = 13 + k * 4;
            const cycles = 3.5;
            const points = [];
            const steps = 40;
            for (let s = 0; s <= steps; s++) {
              const t = s / steps;
              const y = 70 + t * 640;
              const x = 200 + Math.sin(t * cycles * Math.PI * 2 + k * Math.PI) * amp;
              points.push(`${s === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
            }
            return (
              <motion.path
                key={`ribbon-${k}`}
                d={points.join(' ')}
                stroke={k === 0 ? 'rgba(255,225,150,0.55)' : 'rgba(255,240,200,0.4)'}
                strokeWidth={k === 0 ? 0.7 : 0.5}
                fill="none"
                strokeLinecap="round"
                strokeDasharray="180 60"
                filter="url(#gemGlow)"
                animate={{
                  strokeDashoffset: [0, -240],
                  opacity: [0.15, 0.55, 0.15],
                }}
                transition={{
                  strokeDashoffset: { duration: 22 + k * 6, repeat: Infinity, ease: 'linear' },
                  opacity: { duration: 8 + k * 2, repeat: Infinity, ease: 'easeInOut' },
                }}
              />
            );
          })}

          {/* ─── LUMINOUS TRAIL behind the descending pulse (visibly liquid) ───
              A soft gradient stripe from the crown down to the current
              waveY — the viewer can trace the pulse's journey. */}
          <defs>
            <linearGradient id="pulseTrail" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"   stopColor="rgba(255,245,215,0)" />
              <stop offset="40%"  stopColor="rgba(255,235,180,0.14)" />
              <stop offset="85%"  stopColor="rgba(255,220,140,0.28)" />
              <stop offset="100%" stopColor="rgba(255,250,225,0.35)" />
            </linearGradient>
          </defs>
          {waveY > 65 && (
            <>
              {/* Soft outer river — the transformed channel that
                  the pulse leaves in its wake. Wider, heavily blurred,
                  never opaque — feels like warmth landing on the
                  vertebrae rather than a solid beam. */}
              <rect
                x={200 - (28 + channelBoost * 36) / 2}
                y={60}
                width={28 + channelBoost * 36}
                height={waveY - 60}
                fill="url(#pulseTrail)"
                opacity={0.18 + channelBoost * 0.22}
                rx="20"
                filter="url(#wideGemGlow)"
                style={{ mixBlendMode: 'screen' }}
              />
            </>
          )}

          {/* ─── DESCENDING CONSCIOUSNESS PULSE — the leading bead ─── */}
          <motion.circle
            cx="200" cy={waveY} r="6"
            fill="rgba(255,250,225,1)"
            filter="url(#gemGlow)"
            style={{ mixBlendMode: 'screen' }}
          />
          <motion.circle
            cx="200" cy={waveY} r="26"
            fill="url(#gemCore)"
            opacity="0.85"
            filter="url(#wideGemGlow)"
            style={{ mixBlendMode: 'screen' }}
          />

          {/* ═════════ Each vertebra gem ═════════ */}
          {VERTEBRAE.map((v, i) => {
            const breathScale = 1 + (isActive ? breath * 0.06 : 0);
            const cy = 100 + v.y * 560 + vertebraOffset(i) * 8;
            const w = 42 * v.size * breathScale;
            const h = 36 * v.size * breathScale;
            const isSite = i === SURGERY_INDEX;
            // Cumulative gold "fill level" per vertebra. Each vertebra
            // banks a little more light every time the wave passes it.
            // Deeper vertebrae fill slightly faster (they're bathed later
            // in each cycle so they carry the settled brightness).
            const perVertebraBias = (i / (VERTEBRAE.length - 1)) * 0.15;
            const filledLevel = Math.min(1, goldSaturation + perVertebraBias
              + (waveY > cy + 8 ? 0.12 : 0));
            // Excitement: how close the descending pulse is to this gem.
            // 55px reach on either side for a graceful hand-off. Once the
            // wave has PASSED this gem, we retain a soft integrated glow
            // so the viewer sees healing accumulate down the spine.
            let excitement = Math.max(0, 1 - Math.abs(cy - waveY) / 55);
            const hasPassed = waveY > cy + 8;
            // Passed vertebrae now retain a *cumulative* glow that
            // brightens with every dose (filledLevel), not a flat value.
            const passedGlow = hasPassed
              ? 0.35 + filledLevel * 0.5 + sacralBloom * 0.2
              : filledLevel * 0.3;
            excitement = Math.max(excitement, passedGlow);
            // Narration-linked boost (voice mentions this vertebra by name)
            if (narrationTarget === v.id) excitement = Math.max(excitement, 0.95);
            else if (narrationTarget === 'spine' || narrationTarget === 'all') {
              excitement = Math.max(excitement, 0.5);
            }
            // Gems breathe (not pulse): a slow scale envelope that expands
            // as the wave arrives and gently contracts after. Also grows
            // very gently with cumulative fill (a "well-fed" vertebra).
            // Impact flash — while the pulse is directly OVER this
            // vertebra (within 14px), the gem briefly bathes in pure
            // white before settling back to gold. Reads as "the energy
            // has been absorbed and is transforming this spot".
            const impactAmount = Math.max(0, 1 - Math.abs(cy - waveY) / 14);
            const excitedScale = 1 + excitement * 0.18 + filledLevel * 0.05 + impactAmount * 0.12;
            const coreOp = 0.7 + arrivalGlow * 0.2 + breath * 0.08
              + excitement * 0.35
              + filledLevel * 0.25
              + impactAmount * 0.25
              + (isSite ? siteHealAmount * 0.25 : 0);
            const haloR = (60 + excitement * 22 + filledLevel * 14 + impactAmount * 20) * v.size + breath * 6;

            return (
              <g key={v.id} style={{ transform: `translate(0, 0)` }}>
                {/* Arrival ripple — soft ring expanding outward at
                    the exact moment the pulse crosses this vertebra.
                    Now with THREE staggered concentric rings so the
                    "transformation" reads clearly. */}
                {Math.abs(cy - waveY) < 30 && (
                  <>
                    <motion.circle
                      key={`ripple-a-${Math.floor(waveY / 20)}`}
                      cx={200} cy={cy}
                      fill="none"
                      stroke="rgba(255,250,225,0.9)"
                      strokeWidth="1.4"
                      initial={{ r: w * 0.6, opacity: 1 }}
                      animate={{ r: w * 2.6, opacity: 0 }}
                      transition={{ duration: 1.6, ease: 'easeOut' }}
                    />
                    <motion.circle
                      key={`ripple-b-${Math.floor(waveY / 20)}`}
                      cx={200} cy={cy}
                      fill="none"
                      stroke="rgba(255,230,160,0.65)"
                      strokeWidth="1"
                      initial={{ r: w * 0.5, opacity: 0.85 }}
                      animate={{ r: w * 3.4, opacity: 0 }}
                      transition={{ duration: 2.2, ease: 'easeOut', delay: 0.18 }}
                    />
                    <motion.circle
                      key={`ripple-c-${Math.floor(waveY / 20)}`}
                      cx={200} cy={cy}
                      fill="none"
                      stroke="rgba(220,200,140,0.35)"
                      strokeWidth="0.7"
                      initial={{ r: w * 0.4, opacity: 0.6 }}
                      animate={{ r: w * 4.4, opacity: 0 }}
                      transition={{ duration: 2.8, ease: 'easeOut', delay: 0.32 }}
                    />
                    {/* Six sparks flying outward — like the vertebra
                        exhales a burst of light as the wave crosses it.
                        AT THE SURGERY SITE (L4) the sparks re-aim
                        specifically toward the pinched-nerve side —
                        they arc into the red inflammation and dissolve
                        it as they land. */}
                    {[0, 60, 120, 180, 240, 300].map((deg, k) => {
                      // Base radial trajectory
                      let rad = (deg * Math.PI) / 180;
                      let dx = Math.cos(rad) * w * 2.2;
                      let dy = Math.sin(rad) * w * 1.6;
                      // At the surgery site, override 4 of the 6 sparks
                      // to converge on the pinched nerve node (right
                      // side, ~+w*0.7+8 x, +3 y from cy — the first
                      // dot of the nerve constellation).
                      if (isSite) {
                        const targetX = 200 + (w * 0.7 + 8);
                        const targetY = cy + 3;
                        // Deterministic subset by index so the "healing
                        // stream" reads as a consistent arc, not random.
                        if (k !== 3 && k !== 4) {
                          const jitter = (k - 2) * (w * 0.28);
                          dx = (targetX - 200) + jitter * 0.35;
                          dy = (targetY - cy) + jitter * 0.55;
                        }
                      }
                      return (
                        <motion.circle
                          key={`spark-${deg}-${Math.floor(waveY / 20)}`}
                          cx={200} cy={cy}
                          r={isSite ? 1.9 : 1.5}
                          fill={isSite ? "rgba(255,240,200,1)" : "rgba(255,250,225,1)"}
                          filter="url(#gemGlow)"
                          initial={{ opacity: 1, cx: 200, cy: cy }}
                          animate={{ opacity: 0, cx: 200 + dx, cy: cy + dy }}
                          transition={{ duration: isSite ? 1.9 : 1.4, ease: 'easeOut', delay: 0.08 }}
                        />
                      );
                    })}
                    {/* At L4 the arriving pulse also stamps a single
                        "healing wash" burst directly over the red
                        inflammation — a large soft gold glow that
                        expands + dissolves, pulling the redness with it. */}
                    {isSite && (
                      <motion.circle
                        key={`nerve-wash-${Math.floor(waveY / 20)}`}
                        cx={200 + (w * 0.7 + 8)}
                        cy={cy + 3}
                        fill="rgba(255,240,200,0.6)"
                        filter="url(#wideGemGlow)"
                        initial={{ r: 2, opacity: 0 }}
                        animate={{ r: w * 1.8, opacity: [0, 0.85, 0] }}
                        transition={{ duration: 2.4, ease: 'easeOut', delay: 0.15, times: [0, 0.35, 1] }}
                      />
                    )}
                  </>
                )}
                {/* Wide golden halo — expands with excitement */}
                <ellipse
                  cx={200} cy={cy}
                  rx={haloR}
                  ry={haloR * 0.85}
                  fill="url(#gemHalo)"
                  opacity={0.5 + arrivalGlow * 0.2 + breath * 0.08 + excitement * 0.3}
                  filter="url(#wideGemGlow)"
                />

                {/* Saturn ring — horizontal ellipse behind gem */}
                <motion.ellipse
                  cx={200} cy={cy + 1}
                  rx={w * 1.6 * excitedScale}
                  ry={w * 0.28 * excitedScale}
                  fill="none"
                  stroke="url(#ringGrad)"
                  strokeWidth="0.7"
                  opacity={0.5 + arrivalGlow * 0.25 + breath * 0.08 + excitement * 0.35}
                />
                <motion.ellipse
                  cx={200} cy={cy + 1}
                  rx={w * 1.9 * excitedScale}
                  ry={w * 0.32 * excitedScale}
                  fill="none"
                  stroke="url(#ringGrad)"
                  strokeWidth="0.4"
                  opacity={0.28 + arrivalGlow * 0.15 + excitement * 0.25}
                />

                {/* Lens-flare star rays (4-point) */}
                {[0, 45, 90, 135].map((deg) => (
                  <motion.line
                    key={deg}
                    x1={200 - w * 1.2}
                    y1={cy}
                    x2={200 + w * 1.2}
                    y2={cy}
                    stroke="url(#ringGrad)"
                    strokeWidth={deg === 0 || deg === 90 ? 0.6 : 0.35}
                    opacity={0.32 + arrivalGlow * 0.25 + breath * 0.1 + excitement * 0.4}
                    transform={`rotate(${deg} 200 ${cy})`}
                    filter="url(#gemGlow)"
                  />
                ))}

                {/* The gem itself — left face (scales with excitement) */}
                <g style={{ transformOrigin: `200px ${cy}px`, transform: `scale(${excitedScale})` }}>
                  <motion.path
                    d={gemPath(200, cy, w, h)}
                    fill="url(#diamondFace)"
                    opacity={0.88 + excitement * 0.12}
                    filter="url(#gemGlow)"
                  />
                  <motion.path
                    d={`
                      M 200 ${cy - h * 0.55}
                      L 200 ${cy + h * 0.55}
                      L ${200 + w * 0.28} ${cy + h * 0.05}
                      L ${200 + w * 0.5} ${cy - h * 0.15}
                      Z
                    `}
                    fill="url(#diamondFaceR)"
                    opacity={0.5 + excitement * 0.2}
                  />
                  {/* Inner gem core — brightens dramatically as pulse arrives */}
                  <motion.circle
                    cx={200}
                    cy={cy - h * 0.05}
                    r={w * 0.28}
                    fill="url(#gemCore)"
                    opacity={coreOp}
                  />
                  {/* Center-point sparkle */}
                  <motion.circle
                    cx={200}
                    cy={cy - h * 0.05}
                    r={1.6 + excitement * 1.2}
                    fill="rgba(255,255,240,1)"
                    opacity={0.6 + excitement * 0.4}
                  />
                  {/* Prismatic edge */}
                  <line
                    x1={200 - w * 0.44}
                    y1={cy - h * 0.13}
                    x2={200 - w * 0.05}
                    y2={cy - h * 0.5}
                    stroke="url(#prism)"
                    strokeWidth="0.7"
                    opacity={0.55 + arrivalGlow * 0.25 + excitement * 0.3}
                  />
                </g>

                {/* Vertebra label */}
                <text
                  x={200 - w * 1.8}
                  y={cy + 4}
                  fill={`rgba(230,215,180,${0.35 + excitement * 0.4})`}
                  fontSize="11"
                  fontFamily="Manrope, sans-serif"
                  textAnchor="end"
                  letterSpacing="0.3em"
                >
                  {v.id}
                </text>

                {/* Emerging particles — sparkle out from gems as excitement peaks.
                    Each gem has 3 particles orbiting/drifting on independent loops. */}
                {[0, 1, 2].map((k) => {
                  const angle = (k / 3) * Math.PI * 2;
                  const orbitR = w * 1.35;
                  const initCx = 200 + Math.cos(angle) * orbitR * 0.4;
                  const initCy = cy - 2;
                  return (
                    <motion.circle
                      key={`emit-${k}`}
                      cx={initCx}
                      cy={initCy}
                      r={1.2 + (k % 2) * 0.5}
                      fill="rgba(255,240,200,0.95)"
                      filter="url(#gemGlow)"
                      style={{ mixBlendMode: 'screen' }}
                      animate={{
                        cx: [
                          initCx,
                          200 + Math.cos(angle) * orbitR,
                          200 + Math.cos(angle + 0.7) * orbitR * 0.6,
                          initCx,
                        ],
                        cy: [
                          initCy,
                          cy + Math.sin(angle) * orbitR * 0.5 - 6,
                          cy - w * 0.9,
                          initCy,
                        ],
                        opacity: [0, 0.6 + excitement * 0.4, 0.3, 0],
                      }}
                      transition={{
                        duration: 6 + k * 1.2 + (i % 3) * 0.7,
                        repeat: Infinity,
                        delay: i * 0.35 + k * 1.8,
                        ease: 'easeInOut',
                      }}
                    />
                  );
                })}

                {/* Constellation nerve — 3 star nodes on each side */}
                {[-1, 1].map((s) => {
                  // Inflammation fades not just with the scripted phase
                  // (nerveCalmAmount) but also as cumulative healing
                  // (goldSaturation) accrues — so redness visibly
                  // lightens as more energy pours through.
                  const cumulativeCalm = Math.min(1, nerveCalmAmount + goldSaturation * 0.7);
                  const isPinched = isSite && s === 1 && cumulativeCalm < 1;
                  const inflamedFade = 1 - cumulativeCalm; // 0..1
                  const dots = [
                    { x: 200 + s * (w * 0.7 + 8), y: cy + 3 },
                    { x: 200 + s * (w * 0.7 + 26), y: cy + 12 },
                    { x: 200 + s * (w * 0.7 + 44), y: cy + 20 },
                  ];
                  return (
                    <g key={s}>
                      <path
                        d={`M ${200 + s * w * 0.5} ${cy} L ${dots[0].x} ${dots[0].y} L ${dots[1].x} ${dots[1].y} L ${dots[2].x} ${dots[2].y}`}
                        stroke={isPinched ? 'url(#nerveInflamed)' : 'url(#nerveCalmed)'}
                        strokeWidth={isPinched ? 1.6 - goldSaturation * 0.6 : 0.9}
                        fill="none"
                        opacity={0.5 + arrivalGlow * 0.2 + excitement * 0.25}
                        filter="url(#gemGlow)"
                      />
                      {dots.map((d, k) => (
                        <motion.circle
                          key={k}
                          cx={d.x} cy={d.y}
                          r={1.4 + (isPinched ? 0.6 * inflamedFade : 0)}
                          fill={isPinched
                            // Nodes: interpolate red→cool-blue as inflamedFade shrinks
                            ? `rgba(${Math.round(255 - 35 * (1 - inflamedFade))},${Math.round(150 + 90 * (1 - inflamedFade))},${Math.round(120 + 135 * (1 - inflamedFade))},0.95)`
                            : 'rgba(220,240,255,0.95)'}
                          filter="url(#gemGlow)"
                          animate={{ opacity: [0.4, 0.95, 0.4] }}
                          transition={{ duration: 2.5 + k * 0.3, repeat: Infinity, delay: k * 0.4, ease: 'easeInOut' }}
                        />
                      ))}
                      {isPinched && (
                        <circle
                          cx={dots[0].x} cy={dots[0].y}
                          r={(12 + breath * 4) * inflamedFade}
                          fill="url(#nerveInflamed)"
                          opacity={inflamedFade * (0.55 + breath * 0.2)}
                          filter="url(#wideGemGlow)"
                        />
                      )}
                      {isSite && s === 1 && cumulativeCalm > 0 && (
                        <circle
                          cx={dots[0].x} cy={dots[0].y}
                          r={15 + breath * 5}
                          fill="url(#nerveCalmed)"
                          opacity={cumulativeCalm * (0.55 + breath * 0.15)}
                          filter="url(#wideGemGlow)"
                        />
                      )}
                    </g>
                  );
                })}

                {/* Site-heal pulse (only at L4) */}
                {isSite && phase === 'site_heal' && (
                  <motion.circle
                    cx={200} cy={cy}
                    r={40}
                    fill="none"
                    stroke="rgba(255,225,150,0.55)"
                    strokeWidth="1.2"
                    initial={{ r: 18, opacity: 0.75 }}
                    animate={{ r: [18, 65, 18], opacity: [0.75, 0, 0.75] }}
                    transition={{ duration: 3.8, repeat: Infinity, ease: 'easeOut' }}
                  />
                )}
              </g>
            );
          })}

          {/* ═════════ SACRUM — the big terminal diamond ═════════ */}
          {(() => {
            const cy = 100 + SACRUM_Y * 560;
            const w = 82;
            const h = 108;
            return (
              <g>
                {/* Sacral bloom — a warm outer halo that swells when
                    the wave arrives, then radiates through the whole
                    spine as a completion signal. */}
                {sacralBloom > 0.01 && (
                  <>
                    <ellipse
                      cx={200} cy={cy}
                      rx={110 + sacralBloom * 120}
                      ry={95 + sacralBloom * 100}
                      fill="url(#gemHalo)"
                      opacity={sacralBloom * 0.85}
                      filter="url(#wideGemGlow)"
                    />
                    {/* Completion wave rippling upward through the spine */}
                    <motion.line
                      x1="200" y1={cy} x2="200" y2={60}
                      stroke="rgba(255,235,180,0.55)"
                      strokeWidth={2 + sacralBloom * 2}
                      strokeLinecap="round"
                      opacity={sacralBloom * 0.55}
                      filter="url(#gemGlow)"
                    />
                  </>
                )}
                {/* Halo */}
                <ellipse
                  cx={200} cy={cy}
                  rx={92 + breath * 8}
                  ry={82 + breath * 8}
                  fill="url(#gemHalo)"
                  opacity={0.55 + arrivalGlow * 0.25 + breath * 0.1}
                  filter="url(#wideGemGlow)"
                />
                {/* Star rays */}
                {[0, 90, 45, 135].map((deg) => (
                  <line
                    key={deg}
                    x1={200 - w * 1.5}
                    y1={cy}
                    x2={200 + w * 1.5}
                    y2={cy}
                    stroke="url(#ringGrad)"
                    strokeWidth={deg === 0 || deg === 90 ? 0.7 : 0.4}
                    opacity={0.4 + arrivalGlow * 0.3 + breath * 0.1}
                    transform={`rotate(${deg} 200 ${cy})`}
                    filter="url(#gemGlow)"
                  />
                ))}
                {/* Large diamond */}
                <path
                  d={`
                    M 200 ${cy - h * 0.4}
                    L ${200 - w * 0.5} ${cy - h * 0.1}
                    L ${200 - w * 0.15} ${cy + h * 0.1}
                    L 200 ${cy + h * 0.6}
                    L ${200 + w * 0.15} ${cy + h * 0.1}
                    L ${200 + w * 0.5} ${cy - h * 0.1}
                    Z
                  `}
                  fill="url(#diamondFace)"
                  opacity={0.85}
                  filter="url(#gemGlow)"
                />
                {/* Right facet */}
                <path
                  d={`
                    M 200 ${cy - h * 0.4}
                    L 200 ${cy + h * 0.6}
                    L ${200 + w * 0.15} ${cy + h * 0.1}
                    L ${200 + w * 0.5} ${cy - h * 0.1}
                    Z
                  `}
                  fill="url(#diamondFaceR)"
                  opacity={0.5}
                />
                {/* Inner core */}
                <circle
                  cx={200} cy={cy - h * 0.02}
                  r={w * 0.28}
                  fill="url(#gemCore)"
                  opacity={0.85 + arrivalGlow * 0.15 + breath * 0.1}
                />
                {/* Center sparkle */}
                <motion.circle
                  cx={200} cy={cy - h * 0.02}
                  r={2.2}
                  fill="rgba(255,255,240,1)"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                {/* Label */}
                <text
                  x={200 - w * 1.5}
                  y={cy + 4}
                  fill="rgba(230,215,180,0.4)"
                  fontSize="11"
                  fontFamily="Manrope, sans-serif"
                  textAnchor="end"
                  letterSpacing="0.3em"
                >S1</text>
              </g>
            );
          })()}

          {/* ═════════ Per-vertebra transformation personalities ═════════
              Fired only while `dwellIdx === i`. Each level gets its own
              gesture so the sequence stops feeling repetitive.
              L1 slow bloom · L2 spirals · L3 radiates · L4 expands · L5 gentle explosion · S1 grounds. */}
          {dwellIdx >= 0 && (() => {
            const i = dwellIdx;
            const v = VERTEBRAE[i];
            if (!v) return null;
            const cy = 100 + v.y * 560;
            const key = `dwell-${i}`;

            if (i === 0) {
              // L1 — SLOW BLOOM: petal-like halo expanding & receding.
              return (
                <g key={key}>
                  {[0, 1, 2].map((k) => (
                    <motion.circle
                      key={`bloom-${k}`}
                      cx={200} cy={cy}
                      fill="none"
                      stroke={`rgba(255,${240 - k * 20},${190 - k * 40},${0.7 - k * 0.15})`}
                      strokeWidth="1"
                      initial={{ r: 20, opacity: 0 }}
                      animate={{ r: [20, 60 + k * 12, 40 + k * 8], opacity: [0, 0.9, 0.5] }}
                      transition={{ duration: 5.5, delay: k * 0.6, ease: 'easeInOut' }}
                    />
                  ))}
                </g>
              );
            }
            if (i === 1) {
              // L2 — SPIRAL: two rotating arcs.
              return (
                <g key={key}>
                  {[0, 1].map((k) => (
                    <motion.path
                      key={`spiral-${k}`}
                      d={`M ${200 + 60} ${cy} A 60 60 0 1 ${k === 0 ? 1 : 0} ${200 - 60} ${cy}`}
                      fill="none"
                      stroke={`rgba(255,${235 - k * 20},${170 - k * 30},0.55)`}
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0, rotate: 0 }}
                      animate={{ pathLength: [0, 1, 1], opacity: [0, 0.85, 0], rotate: k === 0 ? 360 : -360 }}
                      transition={{ duration: 6, ease: 'easeInOut' }}
                      style={{ transformOrigin: `200px ${cy}px` }}
                    />
                  ))}
                </g>
              );
            }
            if (i === 2) {
              // L3 — RADIATES: 8 rays outward.
              return (
                <g key={key}>
                  {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
                    const rad = (deg * Math.PI) / 180;
                    const x2 = 200 + Math.cos(rad) * 90;
                    const y2 = cy + Math.sin(rad) * 60;
                    return (
                      <motion.line
                        key={`ray-${deg}`}
                        x1={200} y1={cy}
                        stroke="rgba(255,235,180,0.8)"
                        strokeWidth="1"
                        strokeLinecap="round"
                        initial={{ x2: 200, y2: cy, opacity: 0 }}
                        animate={{ x2, y2, opacity: [0, 0.9, 0] }}
                        transition={{ duration: 5.5, delay: 0.4, ease: 'easeOut' }}
                      />
                    );
                  })}
                </g>
              );
            }
            if (i === 3) {
              // L4 (surgery site) — EXPANDS OUTWARD: single large soft
              // gold pillow that pushes the redness away as it grows.
              return (
                <motion.ellipse
                  key={key}
                  cx={200} cy={cy}
                  fill="rgba(255,240,200,0.35)"
                  filter="url(#wideGemGlow)"
                  initial={{ rx: 20, ry: 12, opacity: 0 }}
                  animate={{ rx: [20, 130, 100], ry: [12, 70, 55], opacity: [0, 0.9, 0.5] }}
                  transition={{ duration: 6, ease: 'easeOut' }}
                />
              );
            }
            if (i === 4) {
              // L5 — GENTLE EXPLOSION: 12 sparks + bright core.
              return (
                <g key={key}>
                  <motion.circle
                    cx={200} cy={cy}
                    fill="rgba(255,250,225,1)"
                    filter="url(#gemGlow)"
                    initial={{ r: 4, opacity: 1 }}
                    animate={{ r: [4, 26, 18], opacity: [1, 0.95, 0.4] }}
                    transition={{ duration: 5, ease: 'easeOut' }}
                  />
                  {Array.from({ length: 12 }).map((_, k) => {
                    const rad = (k * 30 * Math.PI) / 180;
                    return (
                      <motion.circle
                        key={`boom-${k}`}
                        cx={200} cy={cy}
                        r={1.4}
                        fill="rgba(255,240,190,1)"
                        filter="url(#gemGlow)"
                        initial={{ cx: 200, cy: cy, opacity: 1 }}
                        animate={{ cx: 200 + Math.cos(rad) * 110, cy: cy + Math.sin(rad) * 80, opacity: 0 }}
                        transition={{ duration: 4.5, delay: 0.4, ease: 'easeOut' }}
                      />
                    );
                  })}
                </g>
              );
            }
            if (i === 5) {
              // S1 — GROUNDS: warm root bloom, downward-oriented.
              return (
                <g key={key}>
                  <motion.ellipse
                    cx={200} cy={cy + 12}
                    fill="rgba(255,220,140,0.55)"
                    filter="url(#wideGemGlow)"
                    initial={{ rx: 30, ry: 8, opacity: 0 }}
                    animate={{ rx: [30, 140, 120], ry: [8, 36, 30], opacity: [0, 0.95, 0.7] }}
                    transition={{ duration: 6, ease: 'easeOut' }}
                  />
                </g>
              );
            }
            return null;
          })()}

          {/* ═════════ Cathedral finale ═════════
              After every vertebra has awakened, a wide golden warmth
              gathers along the spine. Diffuse, breathing, alive — not
              a hard column of light. */}
          {cathedral > 0 && (
            <>
              {/* Broad diffuse aura — the outer warmth */}
              <motion.rect
                x={200 - 140}
                y={60}
                width={280}
                height={720 - 60}
                fill="url(#pulseTrail)"
                rx="140"
                filter="url(#wideGemGlow)"
                initial={{ opacity: 0 }}
                animate={{ opacity: cathedral * 0.28 }}
                transition={{ duration: 3 }}
                style={{ mixBlendMode: 'screen' }}
              />
              {/* Inner warmth — still soft, still translucent */}
              <motion.rect
                x={200 - 70}
                y={60}
                width={140}
                height={720 - 60}
                fill="rgba(255,235,190,0.35)"
                rx="70"
                filter="url(#wideGemGlow)"
                initial={{ opacity: 0 }}
                animate={{ opacity: cathedral * 0.35 }}
                transition={{ duration: 3, delay: 0.4 }}
                style={{ mixBlendMode: 'screen' }}
              />
            </>
          )}
        </svg>
      </div>

      {/* Foreground golden particles.
          Three flavours:
          - "drift":  slow ambient floating around the spine (fairy dust)
          - "rising": start below the frame, ascend and are absorbed into the spine
          - "emit":   start near the spine and drift outward, fading */}
      {[...Array(28)].map((_, i) => {
        const kind = i % 3 === 0 ? 'rising' : (i % 3 === 1 ? 'emit' : 'drift');
        if (kind === 'rising') {
          // Renamed: now DESCENDING — start high near the spine, drift
          // downward and outward, fading. Reinforces the downward flow.
          const startLeft = 44 + ((i * 31) % 12);
          return (
            <motion.div
              key={`fp-${i}`}
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 1.8,
                height: 1.8,
                left: `${startLeft}%`,
                top: '10%',
                background: 'rgba(255,235,180,0.95)',
                boxShadow: '0 0 6px rgba(255,220,140,0.75)',
              }}
              animate={{
                y: [0, 260 + (i % 5) * 40],
                x: [0, 12 - ((i * 11) % 24)],
                opacity: [0, 0.85, 0],
                scale: [0.6, 1, 0.3],
              }}
              transition={{
                duration: 11 + (i % 5),
                repeat: Infinity,
                delay: i * 0.9,
                ease: 'easeIn',
              }}
            />
          );
        }
        if (kind === 'emit') {
          // Emerges near the spine centre and drifts outward.
          const dir = i % 2 === 0 ? 1 : -1;
          return (
            <motion.div
              key={`fp-${i}`}
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 1.6,
                height: 1.6,
                left: '50%',
                top: `${18 + (i * 9) % 65}%`,
                background: 'rgba(255,245,205,0.95)',
                boxShadow: '0 0 5px rgba(255,220,140,0.7)',
              }}
              animate={{
                x: [0, dir * (30 + (i % 5) * 10)],
                y: [0, -20 - (i % 4) * 6],
                opacity: [0, 0.85, 0],
                scale: [0.4, 1, 0.6],
              }}
              transition={{
                duration: 8 + (i % 4),
                repeat: Infinity,
                delay: i * 0.5,
                ease: 'easeOut',
              }}
            />
          );
        }
        // drift
        return (
          <motion.div
            key={`fp-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 1.6 + (i % 3) * 0.6,
              height: 1.6 + (i % 3) * 0.6,
              left: `${34 + ((i * 47) % 32)}%`,
              top: `${8 + (i * 11) % 82}%`,
              background: 'rgba(255,235,180,0.9)',
              boxShadow: '0 0 6px rgba(255,220,140,0.6)',
            }}
            animate={{
              y: [0, -14 - (i % 5) * 4, 0],
              opacity: [0.15, 0.7 + arrivalGlow * 0.15, 0.15],
              x: [0, -3 + (i % 3) * 3, 0],
            }}
            transition={{
              duration: 9 + (i % 4),
              repeat: Infinity,
              delay: i * 0.7,
              ease: 'easeInOut',
            }}
          />
        );
      })}

      {/* Phase hint */}
      <div className="absolute bottom-6 left-6 pointer-events-none">
        <motion.p
          key={phase}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 0.34, y: 0 }}
          transition={{ duration: 1.5 }}
          className="text-white/40 text-[10px] tracking-[0.3em] uppercase"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="crystal-phase-hint"
        >
          {phase === 'arrival'      && 'light descending'}
          {phase === 'bath'         && 'golden bath'}
          {phase === 'decompress'   && 'creating space'}
          {phase === 'site_heal'    && 'healing the site'}
          {phase === 'nerve_relief' && 'calming the nerve'}
          {phase === 'integration'  && 'shimmering ease'}
        </motion.p>
      </div>

      {/* Cathedral finale text — appears only when the whole column has
          become one crystalline pillar of light. Very quiet, italic,
          Playfair; no CTAs. */}
      {cathedral > 0.4 && (
        <motion.div
          className="absolute inset-x-0 pointer-events-none text-center px-8"
          style={{ bottom: '12%' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: Math.min(1, (cathedral - 0.4) * 1.7) }}
          transition={{ duration: 2 }}
          data-testid="cathedral-finale-text"
        >
          <p
            className="italic mx-auto"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: 'rgba(255,240,205,0.88)',
              fontSize: '1.15rem',
              lineHeight: 1.5,
              maxWidth: 340,
              textShadow: '0 0 24px rgba(255,220,140,0.6)',
            }}
          >
            Energy now flows freely through your spine.
          </p>
          <p
            className="mt-3 opacity-70 italic mx-auto"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: 'rgba(255,240,205,0.7)',
              fontSize: '0.85rem',
              maxWidth: 300,
            }}
          >
            Every cell remembers perfect alignment.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default CrystalSpineVisualEngine;
